import Highcharts, { Options, SeriesOptionsType } from 'highcharts';
import { TFunction } from 'i18next';
import { MetricType } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/metrics_pb';
import { ChartMetricModel } from '~/shared/models/metrics/ChartMetricModel';
import {
  getSensorReadingLabel,
  getUnit,
  mapMetricValue,
  roundNumericMetric,
} from '~/shared/models/metrics/metric-conversions';
import { SimulationModel } from '~/shared/models/system/SimulationModel';
import { AppTheme } from '~/shared/theme/theme';

type ReservoirSeries = {
  metric: ChartMetricModel;
  name: string;
};

type SeriesCustomOptions = {
  metricType: MetricType;
  tooltipKind: 'reservoir' | 'actuator' | 'precipitation';
  tooltipContext?: string;
};

function isSeriesCustomOptions(value: unknown): value is SeriesCustomOptions {
  return (
    typeof value === 'object' &&
    value !== null &&
    'metricType' in value &&
    'tooltipKind' in value
  );
}

const percentAxisTickPositions = [0, 20, 40, 60, 80, 100];
const percentAxisMax = 105;
const axisTickCount = percentAxisTickPositions.length;
const stateAxisTickPositions = [0, 1];
const secondaryActuatorAxisBaseOffset = 80;
const actuatorMetricTypes = [
  MetricType.CALCULATED_VALVE_HEIGHT_PERCENTAGE,
  MetricType.VALVE_STATE_ON,
  MetricType.PUMP_STATE_ON,
] as const;

export class IdfChartOptionsModel {
  private readonly waterPercentageSeries: ReservoirSeries[];

  private readonly precipitationMetrics: ChartMetricModel[];

  private readonly actuatorMetrics: ChartMetricModel[];

  private readonly productTitleByMetricKey: Map<string, string>;

  constructor(simulation: SimulationModel, metrics: ChartMetricModel[]) {
    const topReservoirs = simulation.getTopReservoirsByCapacity();
    const titleByName = new Map(topReservoirs.map((r) => [r.name, r.title]));
    this.productTitleByMetricKey = new Map(
      simulation.system.products.flatMap((product) =>
        product.availableMetricTargets.flatMap((target) =>
          target.types.map((type) => [
            `${target.resourceName}:${type}`,
            product.title ?? target.resourceName,
          ]),
        ),
      ),
    );

    this.waterPercentageSeries = metrics
      .filter((m) => m.type === MetricType.CALCULATED_WATER_PERCENTAGE)
      .map((metric) => ({
        metric,
        name: titleByName.get(metric.resourceName) ?? metric.resourceName,
      }));

    this.precipitationMetrics = metrics.filter(
      (m) => m.type === MetricType.PRECIPITATION_MM,
    );

    this.actuatorMetrics = metrics.filter((metric) =>
      actuatorMetricTypes.includes(
        metric.type as (typeof actuatorMetricTypes)[number],
      ),
    );
  }

  get hasData(): boolean {
    return this.waterPercentageSeries.length > 0;
  }

  buildChartOptions(t: TFunction, theme: AppTheme): Options {
    const reservoirColors: [string, string, string] = [
      theme.palette.secondary.main,
      theme.palette.secondary.dark,
      theme.palette.secondary.light,
    ];
    const precipitationColor = theme.palette.success.main;
    const actuatorColors: [string, string, string] = [
      theme.palette.primary.main,
      theme.palette.primary.dark,
      theme.palette.primary.light,
    ];
    const percentActuatorMetrics = this.actuatorMetrics.filter(
      (metric) => metric.type === MetricType.CALCULATED_VALVE_HEIGHT_PERCENTAGE,
    );
    const nonPercentActuatorMetrics = this.actuatorMetrics.filter(
      (metric) => metric.type !== MetricType.CALCULATED_VALVE_HEIGHT_PERCENTAGE,
    );
    const actuatorMetricsByGroup = nonPercentActuatorMetrics.reduce(
      (groups, metric) => {
        const metrics = groups.get(metric.axisGroup) ?? [];
        groups.set(metric.axisGroup, [...metrics, metric]);
        return groups;
      },
      new Map<string, ChartMetricModel[]>(),
    );
    const secondaryActuatorGroups = Array.from(actuatorMetricsByGroup.values());
    const precipitationAxisIndex = secondaryActuatorGroups.length + 1;

    const waterFillSeries = this.waterPercentageSeries.map(
      ({ metric, name }, index) => {
        const baseSerie = metric.createSerie(
          t,
          0,
          reservoirColors[index % reservoirColors.length],
        );
        return {
          ...baseSerie,
          type: 'line' as const,
          name,
          showInNavigator: false,
          custom: {
            metricType: metric.type,
            tooltipKind: 'reservoir',
            tooltipContext: name,
          } satisfies SeriesCustomOptions,
          yAxis: 0,
        };
      },
    );

    const precipitationSeries = this.precipitationMetrics.map((metric) => {
      const baseSerie = metric.createSerie(
        t,
        precipitationAxisIndex,
        precipitationColor,
      );
      return {
        ...baseSerie,
        showInNavigator: false,
        yAxis: precipitationAxisIndex,
        custom: {
          metricType: metric.type,
          tooltipKind: 'precipitation',
        } satisfies SeriesCustomOptions,
      };
    });
    const percentageActuatorSeries = percentActuatorMetrics.map(
      (metric, index) => {
        const baseSerie = metric.createSerie(
          t,
          0,
          actuatorColors[index % actuatorColors.length],
        );
        return {
          ...baseSerie,
          showInNavigator: false,
          yAxis: 0,
          custom: {
            metricType: metric.type,
            tooltipKind: 'actuator',
            tooltipContext:
              this.productTitleByMetricKey.get(
                `${metric.resourceName}:${metric.type}`,
              ) ?? metric.resourceName,
          } satisfies SeriesCustomOptions,
        };
      },
    );

    const stateActuatorSeries = secondaryActuatorGroups.flatMap(
      (metrics, groupIndex) =>
        metrics.map((metric, metricIndex) => {
          const axisIndex = groupIndex + 1;
          const baseSerie = metric.createSerie(
            t,
            axisIndex,
            actuatorColors[metricIndex % actuatorColors.length],
          );
          return {
            ...baseSerie,
            showInNavigator: false,
            yAxis: axisIndex,
            custom: {
              metricType: metric.type,
              tooltipKind: 'actuator',
              tooltipContext:
                this.productTitleByMetricKey.get(
                  `${metric.resourceName}:${metric.type}`,
                ) ?? metric.resourceName,
            } satisfies SeriesCustomOptions,
          };
        }),
    );

    const builtValveHeightSeries = [
      ...percentageActuatorSeries,
      ...stateActuatorSeries,
    ];

    const secondaryActuatorAxis = secondaryActuatorGroups.map(
      (metrics, groupIndex) => {
        const axisMetric = metrics[0];
        const uniqueMetrics = Array.from(
          new Map(metrics.map((metric) => [metric.type, metric])).values(),
        );

        return {
          ...axisMetric.valueRange,
          tickPositions: stateAxisTickPositions,
          opposite: false,
          endOnTick: false,
          gridLineWidth: 0,
          offset:
            secondaryActuatorAxisBaseOffset +
            groupIndex * secondaryActuatorAxisBaseOffset,
          title: {
            text: uniqueMetrics
              .map((metric, metricIndex) =>
                metric.getChartLabel({
                  t,
                  color: actuatorColors[metricIndex % actuatorColors.length],
                  noUnit: true,
                }),
              )
              .join('&nbsp;&nbsp;&nbsp;'),
            useHTML: true,
            style: { fontSize: '16px', whiteSpace: 'nowrap' },
          },
          labels: {
            formatter: ({ value }: { value: number | string }) =>
              axisMetric.yAxisLabelFormatter(t, Number(value)),
          },
        };
      },
    );

    const percentAxisTitle = [
      this.waterPercentageSeries[0]?.metric.getChartLabel({
        t,
        color: reservoirColors[0],
        noUnit: true,
      }) ?? '',
      percentActuatorMetrics[0]?.getChartLabel({
        t,
        color: actuatorColors[0],
        noUnit: true,
      }) ?? '',
    ]
      .filter(Boolean)
      .join('&nbsp;&nbsp;&nbsp;');

    const series = [
      ...waterFillSeries,
      ...builtValveHeightSeries,
      ...precipitationSeries,
    ] as SeriesOptionsType[];

    const yAxis = [
      {
        min: 0,
        max: percentAxisMax,
        endOnTick: false,
        tickPositions: percentAxisTickPositions,
        title: {
          text: percentAxisTitle,
          style: { fontSize: '16px', whiteSpace: 'nowrap' },
        },
        labels: { format: '{value}%' },
      },
      ...secondaryActuatorAxis,
      {
        opposite: true,
        min: 0,
        endOnTick: false,
        tickAmount: axisTickCount,
        gridLineWidth: 0,
        title: {
          text:
            this.precipitationMetrics[0]?.getChartLabel({
              t,
              color: precipitationColor,
            }) ?? '',
          style: { fontSize: '16px', color: precipitationColor },
        },
        labels: { format: '{value} mm' },
      },
    ];

    return {
      title: { text: undefined },
      chart: {
        type: 'line',
        animation: false,
        shadow: false,
        plotBorderWidth: 1,
        plotBorderColor: theme.palette.supportive.light,
        style: { fontFamily: 'Noto Sans' },
      },
      xAxis: {
        type: 'datetime',
        labels: { format: '{value:%H:%M}' },
      },
      yAxis,
      tooltip: {
        shared: true,
        animation: false,
        shadow: false,
        useHTML: true,
        formatter() {
          const header = Highcharts.dateFormat('%H:%M:%S', this.x ?? 0);
          const points = this.points ?? [];
          const rows = points.map((p) => {
            const custom = isSeriesCustomOptions(p.series.userOptions.custom)
              ? p.series.userOptions.custom
              : undefined;
            const metricType = custom?.metricType;
            const label = metricType
              ? getSensorReadingLabel(t, metricType)
              : p.series.name;
            const numericValue = Number(p.y ?? 0);
            const mappedValue = metricType
              ? mapMetricValue(t, metricType, numericValue)
              : numericValue;
            const unit = metricType ? getUnit(metricType) : undefined;
            const value =
              typeof mappedValue === 'number'
                ? unit
                  ? `${roundNumericMetric(metricType!, numericValue)} ${unit}`
                  : `${roundNumericMetric(metricType!, numericValue)}`
                : mappedValue;

            if (custom?.tooltipKind === 'reservoir') {
              return `<span style="color:${p.color}">●</span> ${label} - ${custom.tooltipContext} <b>${value}</b>`;
            }

            if (custom?.tooltipKind === 'actuator') {
              return `<span style="color:${p.color}">●</span> ${label} - ${custom.tooltipContext} <b>${value}</b>`;
            }

            return `<span style="color:${p.color}">●</span> ${label} - <b>${value}</b>`;
          });
          return [header, ...rows].join('<br/>');
        },
      },
      legend: { enabled: false },
      plotOptions: { series: { marker: { enabled: false } } },
      series,
      credits: { enabled: false },
    };
  }
}
